import React from "react";
import { getUnlocked } from "@/lib/trophies";

/**
 * PatronBug — a small "★ PATRON" pill that lives on the CRT cabinet
 * bezel (between the SCARTLEAD.TV wordmark and the CRT-1986 callsign)
 * for viewers who've unlocked the PATRON trophy — i.e. they've actually
 * tipped a coffee.
 *
 * Design intent (moved from a screen overlay to the bezel):
 *   • Sits on the wooden cabinet, NOT over the tape — doesn't clutter
 *     the picture for viewers who chose the "hide distractions" perk.
 *   • Always visible while playback continues (no per-tape fade cycle)
 *     because the bezel is passive real-estate — a persistent gold star
 *     matches the "PBS supporter" vibe better than a blip.
 *   • Self-contained: reads localStorage on mount and listens for the
 *     `crt:trophy-unlocked` custom event so the pill lights up
 *     immediately after the return-from-BMC bumper unlocks PATRON,
 *     without needing a reload.
 *   • Returns null for non-patrons so there's zero DOM cost for
 *     regular viewers.
 */
export function PatronBug() {
  const [isPatron, setIsPatron] = React.useState(false);

  React.useEffect(() => {
    const refresh = () => {
      try { setIsPatron(getUnlocked().has("patron")); }
      catch (e) { setIsPatron(false); }
    };
    refresh();
    // `crt:trophy-unlocked` — dispatched from CRT.js after the
    // PATRON trophy fires so the badge pops on immediately alongside
    // the golden THANK YOU bumper. Also re-check on storage events
    // (other tabs) and window focus (browser back-nav).
    window.addEventListener("crt:trophy-unlocked", refresh);
    window.addEventListener("storage", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("crt:trophy-unlocked", refresh);
      window.removeEventListener("storage", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  if (!isPatron) return null;

  const GOLD = "#f5c542";
  return (
    <div
      className="flex items-center gap-1.5 px-2.5 py-[3px] rounded-full font-mono uppercase"
      style={{
        color: GOLD,
        border: `1px solid ${GOLD}80`,
        backgroundColor: "rgba(0,0,0,0.35)",
        textShadow: `0 0 6px ${GOLD}`,
        boxShadow: `0 0 8px ${GOLD}30`,
        letterSpacing: "0.3em",
        fontSize: "0.6rem",
      }}
      title="You're a patron of the station — thank you!"
      data-testid="patron-bug"
    >
      <span style={{ fontSize: "0.75rem", lineHeight: 1 }}>★</span>
      <span>PATRON</span>
    </div>
  );
}

export default PatronBug;
